import React from "react";
import Button, { ButtonDesign } from "../../components/Button/Button";
import { Layout } from "../../components/Layout/Layout";
import { view } from "@orderofchaos/mvvm";
import { SettingsPageViewModel } from "./SettingsPageViewModel";
import styles from "./SettingsPage.module.css";

export const SettingsPage: React.FC = view(SettingsPageViewModel)(({
  viewModel: {
    realtimeEnabled,
    showSystemEventsInDashboard,
    browserNotificationsEnabled,
    webPushEnabled,
    takenItemsRemindersEnabled,
    aiPhotoAutofillEnabled,
    isNotificationApiSupported,
    notificationPermissionLabel,
    isWebPushSupported,
    webPushStatusLabel,
    canSendWebPushTest,
    canRequestNotificationPermission,
    notificationsError,
    notificationsInfo,
    statusLabel,
    reconnectAttemptsTotal,
    reconnectStormCount,
    canReset,
    onRealtimeEnabledChange,
    onShowSystemEventsChange,
    onBrowserNotificationsEnabledChange,
    onWebPushEnabledChange,
    onTakenItemsRemindersEnabledChange,
    onAiPhotoAutofillEnabledChange,
    requestNotificationPermission,
    sendWebPushTest,
    resetSettings,
  },
}) => {
  return (
    <Layout title={"Настройки"}>
      <section className={styles.root}>
        <article className={styles.card}>
          <header className={styles.cardHeader}>
            <h2>Realtime</h2>
            <span className={styles.badge}>{statusLabel}</span>
          </header>

          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={realtimeEnabled}
              onChange={onRealtimeEnabledChange}
            />
            <span>Включить realtime-канал</span>
          </label>

          <p className={styles.hint}>
            При выключении канал событий отключается и не переподключается до
            включения настройки.
          </p>

          <div className={styles.metrics}>
            <div className={styles.metric}>
              <span className={styles.metricValue}>
                {reconnectAttemptsTotal}
              </span>
              <span className={styles.metricLabel}>reconnect попыток</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricValue}>{reconnectStormCount}</span>
              <span className={styles.metricLabel}>reconnect штормов</span>
            </div>
          </div>
        </article>

        <article className={styles.card}>
          <header className={styles.cardHeader}>
            <h2>Уведомления</h2>
            <span className={styles.badge}>{notificationPermissionLabel}</span>
          </header>

          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={browserNotificationsEnabled}
              onChange={onBrowserNotificationsEnabledChange}
              disabled={!isNotificationApiSupported}
            />
            <span>Включить браузерные уведомления</span>
          </label>

          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={webPushEnabled}
              onChange={onWebPushEnabledChange}
              disabled={!isWebPushSupported || !browserNotificationsEnabled}
            />
            <span>
              Включить Web Push (фоново, даже при закрытом приложении)
            </span>
          </label>

          <p className={styles.hint}>Статус Web Push: {webPushStatusLabel}</p>

          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={takenItemsRemindersEnabled}
              onChange={onTakenItemsRemindersEnabledChange}
              disabled={!isNotificationApiSupported}
            />
            <span>Напоминать о невозвращенных предметах</span>
          </label>

          <p className={styles.hint}>
            Цикл напоминаний: 3ч → 1.5ч → 45м → 20м → повтор до возврата или
            списания.
          </p>

          <div className={styles.inlineActions}>
            <Button
              design={ButtonDesign.ghost}
              onClick={requestNotificationPermission}
              disabled={!canRequestNotificationPermission}
            >
              Запросить разрешение
            </Button>
            <Button
              design={ButtonDesign.ghost}
              onClick={sendWebPushTest}
              disabled={!canSendWebPushTest}
            >
              Тестовый push
            </Button>
          </div>

          {notificationsError ? (
            <div className={styles.error}>{notificationsError}</div>
          ) : null}
          {notificationsInfo ? (
            <div className={styles.success}>{notificationsInfo}</div>
          ) : null}
        </article>

        <article className={styles.card}>
          <header className={styles.cardHeader}>
            <h2>Лента событий</h2>
          </header>

          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={showSystemEventsInDashboard}
              onChange={onShowSystemEventsChange}
            />
            <span>Показывать системные события на главной</span>
          </label>

          <p className={styles.hint}>
            По умолчанию в блоке последних событий показываются только
            пользовательские операции.
          </p>
        </article>

        <article className={styles.card}>
          <header className={styles.cardHeader}>
            <h2>AI</h2>
          </header>

          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={aiPhotoAutofillEnabled}
              onChange={onAiPhotoAutofillEnabledChange}
            />
            <span>Разрешить AI-автозаполнение по фото</span>
          </label>

          <p className={styles.hint}>
            Если выключено, кнопка заполнения по фото в формах будет недоступна.
          </p>
        </article>

        <div className={styles.actions}>
          <Button
            design={ButtonDesign.ghost}
            onClick={resetSettings}
            disabled={!canReset}
          >
            Сбросить настройки
          </Button>
        </div>
      </section>
    </Layout>
  );
});
